import {
  type AbcHistoryProduct,
  type AbcKeepProgress,
  type AbcStatus,
  MATERIAL_BASES,
  MATERIAL_BASIS_LABELS,
  type MeasureItemDef,
  basisName,
  measureItemKey,
  type MaterialBasis,
  type MaterialItem,
  type MaterialOption,
  type AbcOrderTemplateDraft,
  type MaterialTemplate,
  type MaterialTemplateLine,
  type PriceSheetResult,
  type PriceUpdateResult,
  formatQuantity,
  parseQuantity,
  looselyIncludes,
} from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { del, errorMessage, get, patch, post } from "../../api";
import { AbcSearchDialog, abcImage } from "../../components/abc-search";
import { Alert, Badge, Button, Checkbox, Dialog, Empty, Field, Loading, PageHeader, Panel, PanelHeader, SelectField, ago, formatCents, parseDollars } from "../../components/ui";
import { useSettings } from "./useSettings";

/** The company's extra measurement fields (vents, pipe flashings...), from settings. */
function useMeasureItems(): MeasureItemDef[] {
  const { query } = useSettings();
  return query.data?.settings["materials.measurementItems"] ?? [];
}
const basisUnit = (b: MaterialBasis, defs: readonly MeasureItemDef[] = []) =>
  b.startsWith("item:") ? (defs.find((d) => d.key === b.slice(5))?.unit === "ft" ? "ft" : "each") : b === "squares" ? "SQ" : b === "manual" ? "" : "ft";
const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
type History = { id: string; status: "reading" | "ready" | "failed"; since: string; ordersRead: number; ordersTotal: number; products?: AbcHistoryProduct[]; error: string | null; startedAt: string; keeping: AbcKeepProgress | null } | null;

/**
 * Settings → Materials: ABC Supply (account, branch, prices), the catalog (mostly ABC items, from
 * NMT's order history or a search), and material order templates. For material orders and the
 * estimated material cost only: none of it goes to QuickBooks.
 */
export function MaterialSettings() {
  const qc = useQueryClient();
  const items = useQuery({ queryKey: ["material-items"], queryFn: () => get<MaterialItem[]>("/api/material-items") });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["material-items"] });
    void qc.invalidateQueries({ queryKey: ["material-catalog"] });
    void qc.invalidateQueries({ queryKey: ["material-templates"] });
    void qc.invalidateQueries({ queryKey: ["abc"] });
  };
  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Materials" description="What goes on material orders: ABC Supply items with NMT's prices, and templates to start orders from. Used for material orders and the estimated material cost on a job, never sent to QuickBooks." />
      <AbcPanel onChange={refresh} />
      <HistoryPanel onAdded={refresh} />
      {items.isLoading ? <Loading /> : items.error ? <Alert>{errorMessage(items.error)}</Alert> : <CatalogPanel items={items.data!} onChange={refresh} />}
      {items.data && <TemplatesPanel items={items.data} />}
      <DefaultsPanel />
      <MeasureItemsPanel />
    </div>
  );
}

function AbcPanel({ onChange }: { onChange: () => void }) {
  const abc = useQuery({ queryKey: ["abc"], queryFn: () => get<AbcStatus>("/api/abc") });
  const [number, setNumber] = useState("");
  const [prices, setPrices] = useState<PriceUpdateResult | null>(null);
  const shipTo = useMutation({ mutationFn: () => post("/api/abc/ship-to", { number }), onSuccess: () => (setNumber(""), onChange()) });
  const branch = useMutation({ mutationFn: (n: string) => post("/api/abc/branch", { number: n }), onSuccess: onChange });
  const auto = useMutation({ mutationFn: (on: boolean) => post("/api/abc/auto-prices", { on }), onSuccess: onChange });
  const update = useMutation({ mutationFn: () => post<PriceUpdateResult>("/api/material-items/prices", {}), onSuccess: (r) => (setPrices(r), onChange()) });
  const ordering = useMutation({ mutationFn: (on: boolean) => post("/api/abc/ordering", { on }), onSuccess: onChange });
  const check = useMutation({ mutationFn: () => post("/api/abc/ordering-check", {}) });
  if (abc.isLoading) return <Loading />;
  const a = abc.data;
  const err = shipTo.error ?? branch.error ?? auto.error ?? update.error ?? ordering.error;
  return (
    <Panel>
      <PanelHeader
        title="ABC Supply"
        description={!a?.connected ? "Off on this server: it's turned on once ABC approves NMT's live access (or its keys aren't set)." : a.environment === "sandbox" ? "Connected to ABC's sandbox (test data, not NMT's real account). Live access comes once ABC approves it." : "Connected to NMT's ABC account."}
        action={a?.connected && <Badge tone={a.environment === "production" ? "ok" : "wait"}>{a.environment === "production" ? "Live" : "Sandbox"}</Badge>}
      />
      {a?.connected && (
        <div className="flex flex-col gap-4 px-5 py-4 text-sm">
          {err && <Alert>{errorMessage(err)}</Alert>}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <div className="text-xs font-medium text-muted">Ship-to account</div>
              {a.shipTo ? (
                <div>
                  <div className="font-semibold">
                    {a.shipTo.name} <span className="font-normal text-muted">#{a.shipTo.number}</span>
                  </div>
                  <div className="text-ink-soft">{a.shipTo.address}</div>
                </div>
              ) : (
                <p className="text-muted">Not picked yet: prices need it.</p>
              )}
              <form
                className="mt-2 flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (number.trim()) shipTo.mutate();
                }}
              >
                <input className="w-40 rounded-[var(--radius-control)] border border-line bg-white px-2.5 py-1.5 text-base sm:text-sm" placeholder="e.g. 2010466-2" value={number} onChange={(e) => setNumber(e.target.value)} aria-label="Ship-to account number" />
                <Button type="submit" busy={shipTo.isPending}>
                  {a.shipTo ? "Change" : "Use this account"}
                </Button>
              </form>
            </div>
            {a.shipTo && (
              <SelectField label="Branch NMT orders from" value={a.branch?.number ?? ""} onChange={(e) => branch.mutate(e.target.value)}>
                {a.branches.map((b) => (
                  <option key={b.number} value={b.number}>
                    {b.name}
                    {b.home ? " (home branch)" : ""}
                  </option>
                ))}
              </SelectField>
            )}
          </div>
          {a.shipTo && a.branch && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-mist pt-3">
              <Button busy={update.isPending} onClick={() => update.mutate()}>
                Update prices from ABC
              </Button>
              <span className="text-muted">{a.pricesCheckedAt ? `Last checked ${ago(a.pricesCheckedAt)}` : "Not checked yet"}</span>
              <Checkbox label="Check every day" checked={a.autoPrices} onChange={(v) => auto.mutate(v)} />
            </div>
          )}
          {a.shipTo && a.branch && (
            <div className="flex flex-col gap-2 border-t border-mist pt-3">
              <Checkbox
                label="Send material orders to ABC from the CRM"
                description={
                  a.orderingAllowed
                    ? `A draft order gets "Send to ABC": you see exactly what goes (account, branch, delivery, items, ABC's prices) and press Send once. ABC's confirmation and delivery status show on the order and update the calendar.${a.environment === "sandbox" ? " This is ABC's sandbox: test orders only." : ""}`
                    : "Only production sends real orders to ABC."
                }
                checked={a.ordering}
                disabled={!a.orderingAllowed || ordering.isPending}
                onChange={(v) => ordering.mutate(v)}
              />
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="quiet" className="px-2 py-1" busy={check.isPending} onClick={() => check.mutate()}>
                  Check NMT's keys can place orders
                </Button>
                {check.isSuccess && <span className="text-ok">Yes: ABC lets these keys place orders (nothing was ordered).</span>}
                {check.error && <span className="text-danger">{errorMessage(check.error)}</span>}
              </div>
            </div>
          )}
          {prices && (
            <Alert tone={prices.failed.length ? "info" : "ok"}>
              Checked {plural(prices.checked, "item")}: {prices.changed.length ? `${plural(prices.changed.length, "price")} changed` : "no price changes"}.
              {prices.changed.length > 0 && (
                <ul className="mt-1 list-disc pl-5">
                  {prices.changed.map((c) => (
                    <li key={c.id}>
                      {c.name}: {c.fromCents === null ? "no price" : formatCents(c.fromCents)} → {formatCents(c.toCents)}
                    </li>
                  ))}
                </ul>
              )}
              {prices.failed.length > 0 && <div className="mt-1">ABC had no price for: {prices.failed.map((f) => `${f.name} (${f.reason})`).join("; ")}. Their prices stay as they were.</div>}
            </Alert>
          )}
        </div>
      )}
    </Panel>
  );
}

/** Category › subcategory groups, in order (the list comes sorted). */
function groupsOf<T extends { category: string; subcategory: string }>(list: T[]) {
  const out: { title: string; items: T[] }[] = [];
  for (const x of list) {
    const title = [x.category || "Other", x.subcategory].filter(Boolean).join(" › ");
    const g = out.at(-1);
    if (g?.title === title) g.items.push(x);
    else out.push({ title, items: [x] });
  }
  return out;
}

function HistoryPanel({ onAdded }: { onAdded: () => void }) {
  const abc = useQuery({ queryKey: ["abc"], queryFn: () => get<AbcStatus>("/api/abc") });
  const history = useQuery({ queryKey: ["abc-history"], queryFn: () => get<History>("/api/abc/history"), refetchInterval: (q) => (q.state.data?.status === "reading" || (q.state.data?.keeping && !q.state.data.keeping.finishedAt) ? 3000 : false), enabled: !!abc.data?.connected });
  const [since, setSince] = useState(`${new Date().getFullYear()}-01-01`);
  const [off, setOff] = useState<Set<string>>(new Set());
  const [done, setDone] = useState<string | null>(null);
  const start = useMutation({ mutationFn: () => post("/api/abc/history", { since }), onSuccess: () => (setDone(null), void history.refetch()) });
  const h = history.data;
  const fresh = h?.status === "ready" ? (h.products ?? []).filter((p) => !p.inCatalog) : [];
  const keep = useMutation({
    mutationFn: () => post<{ started: number }>("/api/abc/history/keep", { keys: fresh.filter((p) => !off.has(p.key)).map((p) => p.key) }),
    onSuccess: () => (setDone(null), void history.refetch()),
  });
  // Adding runs in the background; the catalog refreshes as products come in.
  const k = h?.keeping ?? null;
  const adding = !!k && !k.finishedAt;
  const lastDone = useRef<number | null>(null);
  useEffect(() => {
    if (k && k.done !== lastDone.current) {
      lastDone.current = k.done;
      onAdded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [k?.done]);
  if (!abc.data?.connected) return null;
  const picked = fresh.filter((p) => !off.has(p.key)).length;
  const toggle = (keys: string[], on: boolean) => {
    const next = new Set(off);
    for (const k of keys) on ? next.delete(k) : next.add(k);
    setOff(next);
  };
  return (
    <Panel>
      <PanelHeader title="Bring in what NMT has ordered" description="Reads every ABC order since a date (phone and myABCSupply orders too) and lists the products used, by ABC's categories: how often, which colors and sizes, and the last price paid. Tick what to keep; each comes in with all its colors and sizes, to pick from on orders." />
      <div className="flex flex-col gap-3 px-5 py-4 text-sm">
        {(start.error || keep.error) && <Alert>{errorMessage(start.error ?? keep.error)}</Alert>}
        {done && <Alert tone="ok">{done}</Alert>}
        {adding && (
          <Alert tone="info">
            Adding {k!.done} of {k!.total} products to the catalog, with all their colors and sizes and ABC's prices. This takes a few minutes; you can leave this page.
          </Alert>
        )}
        {k?.finishedAt && (
          <Alert tone={k.failed.length ? "danger" : "ok"}>
            {plural(k.added, "product")} added to the catalog, with all their colors and sizes.{k.failed.length ? ` Not added: ${k.failed.join("; ")}` : ""}
          </Alert>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Orders since" type="date" value={since} onChange={(e) => setSince(e.target.value)} />
          <Button busy={start.isPending || h?.status === "reading"} disabled={h?.status === "reading"} onClick={() => start.mutate()}>
            {h ? "Read again" : "Read ABC's orders"}
          </Button>
        </div>
        {h?.status === "reading" && (
          <p className="text-ink-soft">
            Reading orders since {h.since}: {h.ordersTotal ? `${h.ordersRead.toLocaleString("en-US")} of ${h.ordersTotal.toLocaleString("en-US")}` : "finding them"}… then looking up each item. It keeps going if you leave this page.
          </p>
        )}
        {h?.status === "failed" && <Alert>{h.error ?? "The read stopped."}</Alert>}
        {h?.status === "ready" && (
          <>
            <p className="text-ink-soft">
              {plural(h.ordersTotal, "order")} since {h.since}: {plural(h.products?.length ?? 0, "product")} ({plural((h.products?.length ?? 0) - fresh.length, "is", "are")} already in the catalog).
            </p>
            {fresh.length > 0 && (
              <>
                <div className="flex gap-3 text-xs">
                  <button className="font-semibold text-brand hover:underline" onClick={() => setOff(new Set())}>
                    Tick all
                  </button>
                  <button className="font-semibold text-brand hover:underline" onClick={() => setOff(new Set(fresh.map((p) => p.key)))}>
                    Untick all
                  </button>
                </div>
                <ul className="max-h-[36rem] overflow-auto rounded-[var(--radius-control)] border border-mist">
                  {groupsOf(fresh).flatMap((g) => {
                    const keys = g.items.map((p) => p.key);
                    const anyOn = keys.some((k) => !off.has(k));
                    return [
                      <li key={`h-${g.title}`} className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-mist bg-white px-3 py-1.5 text-xs font-semibold tracking-wide text-muted uppercase">
                        <span>
                          {g.title} ({g.items.length})
                        </span>
                        <button className="font-semibold normal-case text-brand hover:underline" onClick={() => toggle(keys, !anyOn)}>
                          {anyOn ? "Untick these" : "Tick these"}
                        </button>
                      </li>,
                      ...g.items.map((p) => (
                        <li key={p.key} className="border-b border-mist last:border-0">
                          <Checkbox
                            label={p.name}
                            description={[
                              plural(p.orders, "order"),
                              p.bought
                                .slice(0, 6)
                                .map((b) => `${b.color || b.description || `#${b.itemNumber}`}: ${b.quantity.toLocaleString("en-US")} ${b.unit}${b.lastUnitCents !== null ? ` at ${formatCents(b.lastUnitCents)}` : ""}`)
                                .join(", ") + (p.bought.length > 6 ? `, and ${p.bought.length - 6} more` : ""),
                            ].join(" · ")}
                            checked={!off.has(p.key)}
                            onChange={(v) => toggle([p.key], v)}
                          />
                        </li>
                      )),
                    ];
                  })}
                </ul>
                <div>
                  <Button variant="primary" busy={keep.isPending || adding} disabled={!picked || adding} onClick={() => keep.mutate()}>
                    Add {plural(picked, "product")} to the catalog
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Panel>
  );
}

const priceNote = (o: Pick<MaterialOption, "priceSource" | "pricedAt" | "lastOrderedOn">) => (o.priceSource === "abc" ? `ABC, ${o.pricedAt ? ago(o.pricedAt) : ""}` : o.priceSource === "last_order" ? `Last paid ${o.lastOrderedOn ?? ""}` : o.priceSource === "manual" ? "Typed in" : "");

function CatalogPanel({ items, onChange }: { items: MaterialItem[]; onChange: () => void }) {
  const defs = useMeasureItems();
  const [editing, setEditing] = useState<MaterialItem | "new" | null>(null);
  const [searching, setSearching] = useState(false);
  const [filter, setFilter] = useState("");
  const [sheet, setSheet] = useState<{ result: PriceSheetResult; csv: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const remove = useMutation({ mutationFn: (id: string) => del(`/api/material-items/${id}`), onSuccess: onChange });
  const clear = useMutation({ mutationFn: () => post<{ removed: number }>("/api/material-items/clear", {}), onSuccess: (r) => (setNotice(`${plural(r.removed, "item")} removed.`), onChange()) });
  const readSheet = useMutation({ mutationFn: (csv: string) => post<PriceSheetResult>("/api/material-items/price-sheet", { csv }), onSuccess: (result, csv) => setSheet({ result, csv }) });
  const applySheet = useMutation({ mutationFn: (csv: string) => post<PriceSheetResult>("/api/material-items/price-sheet?apply=1", { csv }), onSuccess: (r) => (setSheet(null), setNotice(`${plural(r.updated.length, "price")} updated.`), onChange()) });
  const shown = filter.trim() ? items.filter((i) => looselyIncludes(`${i.name} ${i.description} ${i.category} ${i.subcategory} ${i.options.map((o) => `${o.itemNumber} ${o.label}`).join(" ")}`, filter)) : items;
  const err = remove.error ?? clear.error ?? readSheet.error ?? applySheet.error;
  return (
    <Panel>
      <PanelHeader
        title={`Catalog (${items.length})`}
        description="Products from ABC come with every color and size ABC carries (picked on each order); prices are per option, from ABC, the last order, or typed in. How a product is counted from a measurement is used by templates."
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" className="px-3 py-1.5" onClick={() => setSearching(true)}>
              Add from ABC
            </Button>
            <Button variant="secondary" className="px-3 py-1.5" onClick={() => setEditing("new")}>
              Add by hand
            </Button>
          </div>
        }
      />
      <input
        ref={fileInput}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) readSheet.mutate(await file.text());
        }}
      />
      <div className="flex flex-col gap-3 px-5 py-4 text-sm">
        {err && <Alert>{errorMessage(err)}</Alert>}
        {notice && <Alert tone="ok">{notice}</Alert>}
        {sheet && (
          <Alert tone="info">
            {sheet.result.updated.length ? `The spreadsheet changes ${plural(sheet.result.updated.length, "price")}:` : "The spreadsheet changes no prices."}
            {sheet.result.updated.length > 0 && (
              <ul className="mt-1 list-disc pl-5">
                {sheet.result.updated.map((u) => (
                  <li key={u.id}>
                    {u.name}: {u.fromCents === null ? "no price" : formatCents(u.fromCents)} → {formatCents(u.toCents)}
                  </li>
                ))}
              </ul>
            )}
            {sheet.result.unmatched.length > 0 && <div className="mt-1">Not matched (left alone): {sheet.result.unmatched.join("; ")}.</div>}
            <div className="mt-2 flex gap-2">
              {sheet.result.updated.length > 0 && (
                <Button variant="primary" busy={applySheet.isPending} onClick={() => applySheet.mutate(sheet.csv)}>
                  Change them
                </Button>
              )}
              <Button onClick={() => setSheet(null)}>Cancel</Button>
            </div>
          </Alert>
        )}
        {items.length > 8 && <input className="w-full max-w-sm rounded-[var(--radius-control)] border border-line bg-white px-2.5 py-1.5 text-base sm:text-sm" placeholder="Find in the catalog (name, color, item #)" value={filter} onChange={(e) => setFilter(e.target.value)} />}
        {!items.length ? (
          <Empty>Nothing yet. Bring in what NMT has ordered from ABC (above), or add products from ABC's catalog.</Empty>
        ) : (
          <ul className="max-h-[48rem] overflow-auto rounded-[var(--radius-control)] border border-mist">
            {groupsOf(shown).flatMap((g) => [
              <li key={`g-${g.title}`} className="sticky top-0 z-10 border-b border-mist bg-white px-3 py-1.5 text-xs font-semibold tracking-wide text-muted uppercase">
                {g.title} ({g.items.length})
              </li>,
              ...g.items.map((i) => {
                const d = i.options.find((o) => o.itemNumber === i.abcItemNumber);
                return (
                  <li key={i.id} className="flex items-center gap-3 border-b border-mist px-3 py-2 last:border-0">
                    {i.imageUrl ? <img src={abcImage(i.imageUrl)} alt="" className="h-9 w-9 shrink-0 rounded object-contain" loading="lazy" /> : <div className="h-9 w-9 shrink-0 rounded bg-mist" />}
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{i.name}</div>
                      <div className="text-xs text-muted">
                        {[
                          !i.options.length ? "Typed in" : i.options.length > 1 ? `${i.options.length} colors and sizes (usual: ${d?.label ?? "none"})` : `ABC #${i.abcItemNumber}`,
                          i.unit,
                          i.basis === "manual" ? null : `${i.per} per ${formatQuantity(i.coverage)} ${basisUnit(i.basis, defs)} (${basisName(i.basis, defs).toLowerCase()})`,
                          i.timesOrdered ? `ordered ${plural(i.timesOrdered, "time")}` : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="font-semibold">{i.unitCents === null ? <span className="text-wait">No price</span> : `${formatCents(i.unitCents)}/${i.unit}`}</div>
                      <div className="text-xs text-muted">{priceNote(d ?? i)}</div>
                    </div>
                    <Button variant="quiet" className="shrink-0 px-2 py-1" onClick={() => setEditing(i)}>
                      Edit
                    </Button>
                    <Button variant="quiet" className="shrink-0 px-2 py-1" aria-label={`Remove ${i.name}`} onClick={() => window.confirm(`Remove ${i.name} from the catalog? Orders that have it keep their lines.`) && remove.mutate(i.id)}>
                      ×
                    </Button>
                  </li>
                );
              }),
            ])}
          </ul>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          <button className="font-semibold text-brand hover:underline" onClick={() => fileInput.current?.click()}>
            Update prices from a spreadsheet (CSV)
          </button>
          {items.length > 0 && (
            <button className="font-semibold text-danger hover:underline" onClick={() => window.confirm(`Remove all ${items.length} items from the catalog? Orders keep their lines; templates lose them.`) && clear.mutate()}>
              Remove everything
            </button>
          )}
        </div>
      </div>
      {searching && <AbcSearchDialog onClose={() => setSearching(false)} onPick={() => onChange()} />}
      {editing && <ItemDialog item={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => (setEditing(null), onChange())} />}
    </Panel>
  );
}

function ItemDialog({ item, onClose, onSaved }: { item: MaterialItem | null; onClose: () => void; onSaved: () => void }) {
  const defs = useMeasureItems();
  const abcProduct = !!item?.options.length;
  const [name, setName] = useState(item?.name ?? "");
  const [unit, setUnit] = useState(item?.unit ?? "EA");
  const [price, setPrice] = useState(item?.unitCents === null || item?.unitCents === undefined ? "" : (item.unitCents / 100).toFixed(2));
  const [basis, setBasis] = useState<MaterialBasis>(item?.basis ?? "manual");
  const [per, setPer] = useState(String(item?.per ?? 1));
  const [coverage, setCoverage] = useState(formatQuantity(item?.coverage ?? 1000));
  const [defaultOption, setDefaultOption] = useState(item?.abcItemNumber ?? "");
  const [options, setOptions] = useState<MaterialOption[]>(item?.options ?? []);
  const [problem, setProblem] = useState<string | null>(null);
  const save = useMutation({ mutationFn: (body: object) => (item ? patch(`/api/material-items/${item.id}`, body) : post("/api/material-items", body)), onSuccess: onSaved });
  const saveOption = useMutation({ mutationFn: ({ itemNumber, body }: { itemNumber: string; body: object }) => patch<MaterialItem>(`/api/material-items/${item!.id}/options/${encodeURIComponent(itemNumber)}`, body), onSuccess: (r) => setOptions(r.options) });
  const submit = () => {
    setProblem(null);
    const cov = basis === "manual" ? 1000 : parseQuantity(coverage);
    const p = Number(per);
    if (!name.trim()) return setProblem("It needs a name.");
    if (!cov || !Number.isInteger(p) || p < 1) return setProblem("Say how many units cover how much.");
    if (abcProduct) return save.mutate({ name: name.trim(), basis, per: p, coverage: cov, defaultOption: defaultOption || undefined });
    const cents = price.trim() === "" ? null : parseDollars(price);
    if (cents !== null && (cents === undefined || Number.isNaN(cents))) return setProblem("Check the price.");
    if (!unit.trim()) return setProblem("It needs a unit.");
    save.mutate({ name: name.trim(), unit: unit.trim(), unitCents: cents, basis, per: p, coverage: cov });
  };
  return (
    <Dialog
      title={item ? `Edit ${item.name}` : "Add an item by hand"}
      onClose={onClose}
      wide={abcProduct}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={save.isPending} onClick={submit}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        {(problem || save.error || saveOption.error) && <Alert>{problem ?? errorMessage(save.error ?? saveOption.error)}</Alert>}
        {abcProduct && item && (
          <p className="text-muted">
            ABC: {[item.category, item.subcategory].filter(Boolean).join(" › ")}
          </p>
        )}
        <Field label="Name on orders" value={name} maxLength={160} onChange={(e) => setName(e.target.value)} />
        {!abcProduct && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Unit" value={unit} maxLength={10} onChange={(e) => setUnit(e.target.value)} />
            <Field label="NMT's price per unit" inputMode="decimal" placeholder="Not priced" value={price} onChange={(e) => setPrice(e.target.value)} />
          </div>
        )}
        <SelectField label="Counted from a measurement by" value={basis} onChange={(e) => setBasis(e.target.value as MaterialBasis)}>
          {MATERIAL_BASES.map((b) => (
            <option key={b} value={b}>
              {b === "manual" ? "Not counted (typed in on each order)" : MATERIAL_BASIS_LABELS[b]}
            </option>
          ))}
          {defs.length > 0 && (
            <optgroup label="Measurement items">
              {defs.map((d) => (
                <option key={d.key} value={`item:${d.key}`}>
                  {d.name} ({d.unit === "ft" ? "ft" : "each"})
                </option>
              ))}
            </optgroup>
          )}
        </SelectField>
        {basis !== "manual" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Units" inputMode="numeric" value={per} onChange={(e) => setPer(e.target.value)} />
            <Field label={`cover this many ${basisUnit(basis, defs)}`} inputMode="decimal" value={coverage} onChange={(e) => setCoverage(e.target.value)} hint={basis === "squares" ? "Shingles: 3 units per 1 SQ" : "Starter: 1 per 105 ft"} />
          </div>
        )}
        {abcProduct && (
          <>
            {options.length > 1 && (
              <SelectField label="Usual option (for estimates before one is picked)" value={defaultOption} onChange={(e) => setDefaultOption(e.target.value)}>
                {options.map((o) => (
                  <option key={o.itemNumber} value={o.itemNumber}>
                    {o.label}
                    {o.timesOrdered ? ` (ordered ${o.timesOrdered}×)` : ""}
                  </option>
                ))}
              </SelectField>
            )}
            <div className="text-xs font-medium text-muted">Options: unit ordered in, and price (blank: ABC's)</div>
            <ul className="max-h-[22rem] overflow-auto rounded-[var(--radius-control)] border border-mist">
              {options.map((o) => (
                <OptionRow key={o.itemNumber} o={o} onSave={(body) => saveOption.mutate({ itemNumber: o.itemNumber, body })} />
              ))}
            </ul>
          </>
        )}
      </div>
    </Dialog>
  );
}

function OptionRow({ o, onSave }: { o: MaterialOption; onSave: (body: object) => void }) {
  const [price, setPrice] = useState(o.priceSource === "manual" && o.unitCents !== null ? (o.unitCents / 100).toFixed(2) : "");
  return (
    <li className="flex flex-wrap items-center gap-2 border-b border-mist px-3 py-2 last:border-0">
      <div className="min-w-0 flex-1">
        <div className="font-medium">{o.label}</div>
        <div className="text-xs text-muted">
          #{o.itemNumber}
          {o.dimensional && " · cut to length"}
          {o.timesOrdered ? ` · ordered ${o.timesOrdered}×` : ""}
        </div>
      </div>
      <select className="rounded-[var(--radius-control)] border border-line bg-white px-2 py-1 text-base sm:text-sm" value={o.unit} onChange={(e) => onSave({ unit: e.target.value })} aria-label="Unit">
        {o.units.map((u) => (
          <option key={u.code} value={u.code}>
            {u.code} ({u.name})
          </option>
        ))}
      </select>
      <span className="w-24 text-right">
        {o.unitCents === null ? <span className="text-wait">No price</span> : formatCents(o.unitCents)}
        <span className="block text-xs text-muted">{priceNote(o)}</span>
      </span>
      <input
        className="w-20 rounded-[var(--radius-control)] border border-line bg-white px-2 py-1 text-base sm:text-sm"
        inputMode="decimal"
        placeholder="ABC's"
        value={price}
        aria-label={`Price for ${o.label}`}
        onChange={(e) => setPrice(e.target.value)}
        onBlur={() => {
          const cents = price.trim() === "" ? null : parseDollars(price);
          if (cents !== null && (cents === undefined || Number.isNaN(cents))) return;
          if (cents === null ? o.priceSource === "manual" : cents !== o.unitCents || o.priceSource !== "manual") onSave({ unitCents: cents });
        }}
      />
    </li>
  );
}

function TemplatesPanel({ items }: { items: MaterialItem[] }) {
  const qc = useQueryClient();
  const templates = useQuery({ queryKey: ["material-templates"], queryFn: () => get<MaterialTemplate[]>("/api/material-templates") });
  const [editing, setEditing] = useState<MaterialTemplate | "new" | null>(null);
  const [fromOrder, setFromOrder] = useState(false);
  const [draft, setDraft] = useState<AbcOrderTemplateDraft | null>(null);
  const remove = useMutation({ mutationFn: (id: string) => del(`/api/material-templates/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["material-templates"] }) });
  const byId = new Map(items.map((i) => [i.id, i]));
  return (
    <Panel>
      <PanelHeader
        title="Templates"
        description="Start a material order from one: each line a fixed quantity, or counted from the job's measurement report. Tie one to a retail shingle and the proposal's cost estimate uses it."
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" className="px-3 py-1.5" onClick={() => setFromOrder(true)}>
              From an ABC order…
            </Button>
            <Button variant="secondary" className="px-3 py-1.5" disabled={!items.length} onClick={() => setEditing("new")}>
              New template
            </Button>
          </div>
        }
      />
      <div className="px-5 py-4 text-sm">
        {remove.error && <Alert>{errorMessage(remove.error)}</Alert>}
        {templates.isLoading ? (
          <Loading />
        ) : !templates.data?.length ? (
          <Empty>{items.length ? "No templates yet. Make one here, or save any material order as a template." : "Add catalog items first."}</Empty>
        ) : (
          <ul className="rounded-[var(--radius-control)] border border-mist">
            {templates.data.map((t) => (
              <li key={t.id} className="flex items-center gap-3 border-b border-mist px-3 py-2 last:border-0">
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{t.name}</div>
                  <div className="truncate text-xs text-muted">{t.lines.map((l) => byId.get(l.itemId)?.name ?? "(removed item)").join(", ")}</div>
                </div>
                <Button variant="quiet" className="px-2 py-1" onClick={() => setEditing(t)}>
                  Edit
                </Button>
                <Button variant="quiet" className="px-2 py-1" aria-label={`Remove ${t.name}`} onClick={() => window.confirm(`Remove the template "${t.name}"?`) && remove.mutate(t.id)}>
                  ×
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {fromOrder && (
        <FromAbcOrderDialog
          onClose={() => setFromOrder(false)}
          onDrafted={async (d) => {
            // Products it added to the catalog have to be there before the template opens.
            await qc.refetchQueries({ queryKey: ["material-items"] });
            setFromOrder(false);
            setDraft(d);
            setEditing("new");
          }}
        />
      )}
      {editing && (
        <TemplateDialog
          template={editing === "new" ? null : editing}
          draft={editing === "new" ? draft : null}
          items={items}
          onClose={() => (setEditing(null), setDraft(null))}
          onSaved={() => (setEditing(null), setDraft(null), void qc.invalidateQueries({ queryKey: ["material-templates"] }), void qc.invalidateQueries({ queryKey: ["material-catalog"] }))}
        />
      )}
    </Panel>
  );
}

/** Type an ABC order number; its items become a template to look over. */
function FromAbcOrderDialog({ onClose, onDrafted }: { onClose: () => void; onDrafted: (d: AbcOrderTemplateDraft) => Promise<void> }) {
  const [orderNumber, setOrderNumber] = useState("");
  const go = useMutation({ mutationFn: async () => onDrafted(await post<AbcOrderTemplateDraft>("/api/material-templates/from-abc-order", { orderNumber: orderNumber.trim() })) });
  return (
    <Dialog
      title="Template from an ABC order"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={go.isPending} disabled={!orderNumber.trim()} onClick={() => go.mutate()}>
            Get the order
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-3 text-sm"
        onSubmit={(e) => {
          e.preventDefault();
          if (orderNumber.trim()) go.mutate();
        }}
      >
        {go.error && <Alert>{errorMessage(go.error)}</Alert>}
        <Field label="ABC order number" placeholder="2009502268" inputMode="numeric" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} autoFocus />
        <p className="text-muted">Every item on the order goes on the template in the color or size ordered. Products not in the catalog yet are added. Items the catalog counts from a measurement report (shingles, ridge, starter, underlayment, drip edge…) are counted from each job's roof; the rest keep the order's quantity. You look it over before it's saved.</p>
      </form>
    </Dialog>
  );
}

function TemplateDialog({ template, draft, items, onClose, onSaved }: { template: MaterialTemplate | null; draft?: AbcOrderTemplateDraft | null; items: MaterialItem[]; onClose: () => void; onSaved: () => void }) {
  const defs = useMeasureItems();
  const { query } = useSettings();
  const products = query.data?.settings["retail.priceList"].products ?? [];
  const byId = new Map(items.map((i) => [i.id, i]));
  const [name, setName] = useState(template?.name ?? draft?.name ?? "");
  const [productKey, setProductKey] = useState(template?.productKey ?? "");
  const [lines, setLines] = useState<MaterialTemplateLine[]>((template?.lines ?? draft?.lines ?? []).filter((l) => byId.has(l.itemId)));
  const [adding, setAdding] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const save = useMutation({ mutationFn: (body: object) => (template ? patch(`/api/material-templates/${template.id}`, body) : post("/api/material-templates", body)), onSuccess: onSaved });
  const setLine = (i: number, p: Partial<MaterialTemplateLine>) => setLines(lines.map((l, k) => (k === i ? { ...l, ...p } : l)));
  const add = (id: string) => {
    const item = byId.get(id);
    if (!item) return;
    setLines([...lines, { itemId: id, mode: item.basis === "manual" ? "fixed" : "measure", quantity: item.basis === "manual" ? 1 : 0, option: null }]);
    setAdding("");
  };
  return (
    <Dialog
      title={template ? `Edit ${template.name}` : "New template"}
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            busy={save.isPending}
            onClick={() => {
              if (!name.trim()) return setProblem("Give it a name.");
              if (!lines.length) return setProblem("Add at least one item.");
              save.mutate({ name: name.trim(), productKey, lines });
            }}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        {(problem || save.error) && <Alert>{problem ?? errorMessage(save.error)}</Alert>}
        {draft && (
          <Alert tone="info">
            From the ABC order: look it over, rename it, and Save.
            {draft.added.length > 0 && ` Added to the catalog: ${draft.added.join(", ")}.`}
            {draft.skipped.length > 0 && ` Left out: ${draft.skipped.join(" ")}`}
          </Alert>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name" placeholder="Retail shingle roof: Duration" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
          <SelectField label="For a retail shingle (proposal cost estimate)" value={productKey} onChange={(e) => setProductKey(e.target.value)}>
            <option value="">None</option>
            {products.map((p) => (
              <option key={p.key} value={p.key}>
                {p.name}
              </option>
            ))}
          </SelectField>
        </div>
        <ul className="rounded-[var(--radius-control)] border border-mist">
          {lines.map((l, i) => {
            const item = byId.get(l.itemId)!;
            return (
              <li key={`${l.itemId}-${i}`} className="flex flex-wrap items-center gap-2 border-b border-mist px-3 py-2 last:border-0">
                <span className="min-w-0 flex-1 font-medium">
                  {item.name} <span className="text-xs font-normal text-muted">{item.unit}</span>
                </span>
                <select className="rounded-[var(--radius-control)] border border-line bg-white px-2 py-1 text-base sm:text-sm" value={l.mode} onChange={(e) => setLine(i, { mode: e.target.value as "fixed" | "measure" })} aria-label="How many">
                  <option value="measure" disabled={item.basis === "manual"}>
                    {item.basis === "manual" ? "Counted (set how in the catalog)" : `Counted: ${basisName(item.basis, defs).toLowerCase()}`}
                  </option>
                  <option value="fixed">A set number</option>
                </select>
                {l.mode === "fixed" && <input className="w-20 rounded-[var(--radius-control)] border border-line bg-white px-2 py-1 text-base sm:text-sm" inputMode="numeric" value={l.quantity} onChange={(e) => setLine(i, { quantity: Math.max(0, Math.floor(Number(e.target.value) || 0)) })} aria-label="Quantity" />}
                {item.options.length > 1 && (
                  <select className="basis-full rounded-[var(--radius-control)] border border-line bg-white px-2 py-1 text-base sm:basis-auto sm:text-sm" value={l.option ?? ""} onChange={(e) => setLine(i, { option: e.target.value || null })} aria-label="Color or size">
                    <option value="">Color/size: pick on each order</option>
                    {[...item.options]
                      .sort((a, b) => b.timesOrdered - a.timesOrdered)
                      .map((o) => (
                        <option key={o.itemNumber} value={o.itemNumber}>
                          Always {o.label}
                        </option>
                      ))}
                  </select>
                )}
                <Button variant="quiet" className="px-2 py-1" aria-label={`Remove ${item.name}`} onClick={() => setLines(lines.filter((_, k) => k !== i))}>
                  ×
                </Button>
              </li>
            );
          })}
          <li className="px-3 py-2">
            <select className="w-full rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5 text-base sm:text-sm" value={adding} onChange={(e) => add(e.target.value)} aria-label="Add an item">
              <option value="">Add an item from the catalog…</option>
              {items.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name} ({i.unit})
                </option>
              ))}
            </select>
          </li>
        </ul>
      </div>
    </Dialog>
  );
}

function DefaultsPanel() {
  const { query, save } = useSettings();
  const [waste, setWaste] = useState("");
  const [supplier, setSupplier] = useState("");
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    const s = query.data?.settings;
    if (s) {
      setWaste(String(s["materials.wastePercent"]));
      setSupplier(s["materials.supplier"]);
    }
  }, [query.data]);
  const w = Number(waste);
  return (
    <Panel>
      <PanelHeader title="Defaults" />
      <div className="grid items-end gap-4 px-5 py-5 sm:grid-cols-[1fr_1fr_auto]">
        <Field label="Supplier on orders" value={supplier} maxLength={160} onChange={(e) => (setSupplier(e.target.value), setSaved(false))} />
        <Field label="Waste when the report doesn't say (%)" type="number" min={0} max={50} value={waste} onChange={(e) => (setWaste(e.target.value), setSaved(false))} />
        <Button busy={save.isPending} disabled={!Number.isInteger(w) || w < 0 || w > 50} onClick={() => save.mutate({ "materials.wastePercent": w, "materials.supplier": supplier.trim() }, { onSuccess: () => setSaved(true) })}>
          {saved ? "Saved" : "Save"}
        </Button>
      </div>
      {save.error && (
        <div className="px-5 pb-4">
          <Alert>{errorMessage(save.error)}</Alert>
        </div>
      )}
    </Panel>
  );
}

/** The company's extra roof measurement fields: on every measurement, and countable by catalog items. */
function MeasureItemsPanel() {
  const { query, save } = useSettings();
  const [rows, setRows] = useState<MeasureItemDef[] | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    const s = query.data?.settings;
    if (s && rows === null) setRows(s["materials.measurementItems"]);
  }, [query.data, rows]);
  if (!rows) return null;
  const set = (i: number, patch: Partial<MeasureItemDef>) => (setRows(rows.map((r, k) => (k === i ? { ...r, ...patch } : r))), setSaved(false));
  const keys = rows.map((r) => r.key);
  const ok = rows.every((r) => r.name.trim()) && new Set(keys).size === keys.length;
  return (
    <Panel>
      <PanelHeader title="Measurement items" description="Extra things counted on every roof measurement (by hand, from a report, or from the insurance scope): vents, pipe flashings, rain caps… A catalog item can be counted from one (e.g. a pipe boot for each pipe flashing). Scope words: what a carrier's line says, so it's found in the scope." />
      <div className="flex flex-col gap-2 px-5 py-4">
        {rows.map((r, i) => (
          <div key={r.key} className="grid items-end gap-2 sm:grid-cols-[1fr_110px_2fr_auto]">
            <Field label={i === 0 ? "Name" : ""} aria-label="Name" value={r.name} onChange={(e) => set(i, { name: e.target.value })} />
            <SelectField label={i === 0 ? "Counted in" : ""} aria-label="Counted in" value={r.unit} onChange={(e) => set(i, { unit: e.target.value as "ea" | "ft" })}>
              <option value="ea">Each</option>
              <option value="ft">Feet</option>
            </SelectField>
            <Field label={i === 0 ? "Scope words (comma separated)" : ""} aria-label="Scope words" value={r.match} onChange={(e) => set(i, { match: e.target.value })} placeholder="pipe jack, pipe boot" />
            <Button variant="quiet" aria-label={`Remove ${r.name}`} onClick={() => (setRows(rows.filter((_, k) => k !== i)), setSaved(false))}>
              ×
            </Button>
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <Button
            onClick={() => {
              let key = "item";
              for (let n = 1; keys.includes(key); n++) key = `item_${n}`;
              setRows([...rows, { key, name: "", unit: "ea", match: "" }]);
              setSaved(false);
            }}
          >
            + Add an item
          </Button>
          <span className="flex-1" />
          <Button
            variant="primary"
            busy={save.isPending}
            disabled={!ok}
            onClick={() =>
              save.mutate(
                // New ones get a key from their name (kept after, so catalog items counting them stay linked).
                { "materials.measurementItems": rows.map((r) => (r.key.startsWith("item") && /^item(_\d+)?$/.test(r.key) ? { ...r, key: uniqueKey(measureItemKey(r.name), rows) } : r)) },
                { onSuccess: (res) => (setRows(res.settings["materials.measurementItems"]), setSaved(true)) },
              )
            }
          >
            {saved ? "Saved" : "Save"}
          </Button>
        </div>
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      </div>
    </Panel>
  );
}

const uniqueKey = (base: string, rows: readonly MeasureItemDef[]) => {
  let key = base;
  for (let n = 2; rows.some((r) => r.key === key); n++) key = `${base}_${n}`;
  return key;
};
